import { z } from 'zod';
import { catalogItemSchema } from './catalog.js';
import { contentIdSchema } from './common.js';
import { devicePlatformSchema } from './device.js';
import { liveChannelSchema } from './livetv.js';
import { playbackSessionIdSchema, sourceIdSchema } from './playback.js';
/**
 * Пользовательские данные: избранное, история и прогресс просмотра.
 * Прогресс синхронизируется через сервер, поэтому просмотр продолжается
 * на другом устройстве (PROJECT.md §2).
 */
/**
 * Про какую серию идёт речь.
 *
 * Обе части вместе или ни одной: «сезон без серии» никуда не указывает, а
 * отсутствие обеих означает фильм — то есть тайтл целиком.
 *
 * Нужна там, где зритель распоряжается ОДНИМ просмотром со страницы сериала:
 * отмечает серию просмотренной или сбрасывает её прогресс, не задевая соседние.
 */
export const episodeSpotSchema = z
    .object({
    season: z.number().int().nonnegative().optional(),
    episode: z.number().int().positive().optional(),
})
    .refine((value) => (value.season === undefined) === (value.episode === undefined), { message: 'Сезон и серия задаются вместе' });
export const progressUpdateSchema = z.object({
    contentId: contentIdSchema,
    positionMs: z.number().int().min(0).max(86_400_000),
    durationMs: z.number().int().min(0).max(86_400_000).nullable().default(null),
    /** Клиент может явно отметить просмотр завершённым. */
    completed: z.boolean().default(false),
    /**
     * Какая серия смотрится. Обе части вместе или ни одной: прогресс сериала без
     * номера серии — это позиция «где-то в сериале», и вернуться по ней некуда.
     * Отсутствуют — прогресс относится к тайтлу целиком (фильм).
     */
    season: z.number().int().nonnegative().optional(),
    episode: z.number().int().positive().optional(),
    /**
     * Из какого просмотра эта позиция.
     *
     * Нужен не воспроизведению, а ПОДПИСИ: чья это история. Заголовок профиля
     * говорит, кто выбран на устройстве СЕЙЧАС, а последняя позиция уезжает
     * тогда, когда плеер уже закрывают, — и если между началом просмотра и этой
     * отправкой у телевизора сменился зритель, просмотр записывался бы на того,
     * кто только что сел, а не на того, кто смотрел.
     *
     * Необязателен: позицию присылает не только плеер (перенос отметок из
     * медиатеки идёт без всякой сессии), а старый клиент просто не знает про это
     * поле — тогда подписью остаётся выбранный профиль, как и раньше.
     */
    sessionId: playbackSessionIdSchema.optional(),
});
export const progressEntrySchema = z.object({
    contentId: contentIdSchema,
    /** Серия, к которой относится позиция; `null` — тайтл целиком (фильм). */
    season: z.number().int().nonnegative().nullable().default(null),
    episode: z.number().int().positive().nullable().default(null),
    positionMs: z.number().int().nonnegative(),
    durationMs: z.number().int().nonnegative().nullable(),
    percent: z.number().min(0).max(100),
    completed: z.boolean(),
    /**
     * Запись оставлена зрителем вручную (после «сбросить прогресс»). Порог в
     * минуту её не касается: иначе сброшенная карточка исчезала бы из истории
     * сразу, и сброс был бы неотличим от удаления.
     */
    kept: z.boolean().default(false),
    updatedAt: z.string(),
});
/** Элемент «Продолжить просмотр»: карточка + позиция. */
export const continueWatchingItemSchema = z.object({
    item: catalogItemSchema,
    progress: progressEntrySchema,
});
export const continueWatchingResponseSchema = z.object({
    items: z.array(continueWatchingItemSchema),
    /**
     * Идёт ли прямо сейчас сверка с медиатекой.
     *
     * Главная не ждёт её: обход занимает секунды, а полка — первое, что видит
     * зритель, и задерживать её ради чужого сервиса нельзя. Поэтому ответ
     * приходит сразу с тем, что известно, и этим признаком: «показанное может
     * поменяться». Увидев его, клиент дожидается конца сверки
     * (`/me/continue-watching/pending`) и перечитывает полку сам — зрителю не
     * приходится уходить с экрана и возвращаться, чтобы увидеть подтянутое.
     *
     * Необязательное поле: клиент прежней версии его не знает и просто продолжит
     * показывать первый ответ, как делал раньше.
     */
    syncing: z.boolean().optional(),
    /**
     * Номер последней завершённой сверки.
     *
     * Клиент возвращает его, когда приходит ждать: без этого номера обновление
     * полки не работало вовсе. Обход медиатеки, стоящей рядом, укладывается в
     * миллисекунды и успевал закончиться раньше, чем клиент присылал второй
     * запрос, — тому отвечали «ждать нечего», и полка оставалась прежней.
     */
    syncRevision: z.number().int().nonnegative().optional(),
});
export const favoriteRequestSchema = z.object({ contentId: contentIdSchema });
export const favoritesResponseSchema = z.object({
    items: z.array(catalogItemSchema),
    /**
     * Избранные телеканалы — отдельным полем, а не среди `items`.
     *
     * У канала нет карточки каталога: ни года, ни постера, ни описания. Втиснув
     * его в `CatalogItem`, пришлось бы всё это выдумать, а полка избранного
     * показала бы пустую плашку вместо канала. Клиент постарше поля не знает и
     * покажет ровно то, что показывал, — фильмы и сериалы.
     */
    channels: z.array(liveChannelSchema).default([]),
    /** Идентификаторы, карточки которых временно недоступны (источник упал). */
    unavailable: z.array(contentIdSchema),
});
export const historyResponseSchema = z.object({
    entries: z.array(progressEntrySchema),
    /**
     * То же самое, но с карточками. Отдельным полем, а не заменой `entries`:
     * старый клиент продолжает читать позиции, новый — показывает названия и
     * постеры, не запрашивая их по одному.
     */
    items: z.array(continueWatchingItemSchema).default([]),
});
/**
 * Отметки «этой раздачей уже смотрели».
 *
 * Живут НА СЕРВЕРЕ, а не в браузере и не в приставке, по той же причине, что и
 * история: перебирают раздачи на телевизоре, а проверяют потом в браузере, и
 * отметка, оставшаяся на одном устройстве, не отвечает на вопрос «что мы уже
 * пробовали». Владелец тот же, что у истории, — выбранный профиль.
 *
 * ЧИТАЮТСЯ они не отдельным маршрутом, а прямо в источниках: у каждой раздачи
 * есть флаг `played`. Хранятся по стабильному ключу релиза, который сервер
 * считает сам, — `sourceId` у раздачи случайный на каждый поиск.
 */
export const releaseMarkResponseSchema = z.object({
    contentId: contentIdSchema,
    sourceId: sourceIdSchema,
    /** Итоговое состояние отметки — то, что клиент и рисует. */
    marked: z.boolean(),
});
export const releaseMarkRequestSchema = z.object({
    contentId: contentIdSchema,
    sourceId: sourceIdSchema,
    /** `true` — поставить отметку, `false` — снять. */
    marked: z.boolean(),
});
/**
 * История поиска: последние запросы зрителя, свежие первыми.
 *
 * На СЕРВЕРЕ и по профилю — по той же причине, что запомненный выбор и отметки
 * раздач: искал на телевизоре, продолжил в браузере, и подсказка обязана знать
 * об этом. Длина запроса ограничена тем же, чем и сам поиск.
 */
export const searchHistoryRequestSchema = z.object({
    query: z.string().trim().min(1).max(120),
});
export const searchHistoryResponseSchema = z.object({
    queries: z.array(z.string()).default([]),
});
/**
 * Запомненный на тайтл выбор: источник, озвучка, сезон и серия.
 *
 * На СЕРВЕРЕ, а не в localStorage браузера и не в `SharedPreferences` приставки.
 * Причина та же, что у истории и отметок раздач: у одного зрителя несколько
 * устройств, и «мой источник с моей озвучкой» — это свойство человека, а не
 * браузера. Раньше выбор, сделанный на телевизоре, в браузере не значил ничего.
 *
 * Все поля могут быть пустыми: выбор появляется по частям, и запись со всеми
 * полями сразу существует только у того, кто уже досмотрел серию сериала.
 */
export const titleChoiceSchema = z.object({
    contentId: contentIdSchema,
    sourceId: sourceIdSchema.nullable().default(null),
    /** Подпись источника — запасной ключ, когда того же `sourceId` уже нет в выдаче. */
    sourceLabel: z.string().max(200).nullable().default(null),
    voice: z.string().max(200).nullable().default(null),
    season: z.number().int().nonnegative().nullable().default(null),
    episode: z.number().int().positive().nullable().default(null),
});
/**
 * Изменение выбора. Отсутствующее поле НЕ ТРОГАЕТСЯ, `null` — очищается: выбор
 * делают по одному шагу, и присылать всю запись ради смены озвучки значит
 * стирать всё остальное тем, что клиент про него ещё не знает.
 */
export const titleChoiceUpdateSchema = z.object({
    contentId: contentIdSchema,
    sourceId: sourceIdSchema.nullable().optional(),
    sourceLabel: z.string().max(200).nullable().optional(),
    voice: z.string().max(200).nullable().optional(),
    season: z.number().int().nonnegative().nullable().optional(),
    episode: z.number().int().positive().nullable().optional(),
});
/**
 * Панель мониторинга администратора: что происходит в установке прямо сейчас.
 *
 * Собирается из трёх разных источников — живых сессий воспроизведения, записей
 * библиотеки и телепрограммы, — но снаружи это один ответ: администратор
 * смотрит на одну картину, а не сшивает её из трёх запросов.
 */
export const monitorWatchSchema = z.object({
    sessionId: z.string(),
    userId: z.string(),
    userName: z.string(),
    deviceId: z.string(),
    /** Понятное имя устройства («Xiaomi MiBox»), а не один его идентификатор. */
    deviceName: z.string().nullable().default(null),
    /**
     * Версия КЛИЕНТА, с которого смотрят.
     *
     * Нужна, чтобы отличать «приложение не умеет» от «сервер сломался»: половина
     * жалоб с приставки объясняется тем, что на ней стоит сборка постарше
     * серверной, и без этой строчки выяснять её приходилось, идя к телевизору.
     *
     * `null` — клиент о себе не сказал: старая сборка приставки или браузер,
     * который своей версии не имеет вовсе.
     */
    appVersion: z.string().nullable().default(null),
    /** Что смотрят: фильм, серия сериала или телеканал. */
    kind: z.enum(['movie', 'series', 'channel', 'unknown']),
    contentId: z.string(),
    title: z.string(),
    subtitle: z.string().nullable().default(null),
    posterUrl: z.string().nullable().default(null),
    startedAt: z.string(),
    /**
     * Когда сессия последний раз ЧТО-ТО ТЯНУЛА.
     *
     * Не то же, что «жива»: TTL у сессии скользящий и длинный (полчаса), поэтому
     * запись остаётся в реестре ещё долго после того, как зритель выключил
     * телевизор. Отличить одно от другого можно только по этому времени.
     */
    lastSeenAt: z.string(),
    /** Ничего не тянет прямо сейчас: сессия ещё жива, но просмотр остановлен. */
    idle: z.boolean().default(false),
    /**
     * Что делает плеер ПО ЕГО СОБСТВЕННЫМ СЛОВАМ.
     *
     * `null` — клиент молчит (старая версия приложения или страница закрыта без
     * прощания); тогда остаётся прежняя догадка по потоку байт. Всё остальное —
     * не догадка: паузу и буферизацию по трафику не отличить никак.
     */
    state: z.enum(['playing', 'paused', 'buffering', 'ended']).nullable().default(null),
    /** Режим доставки: прямая отдача, ремукс или перекодирование. */
    mode: z.string(),
    hardware: z.string(),
    videoCodec: z.string().nullable().default(null),
    audioCodec: z.string().nullable().default(null),
    height: z.number().int().nullable().default(null),
    bitrateKbps: z.number().int().nullable().default(null),
    forcedBitrateKbps: z.number().int().nullable().default(null),
    forcedHeight: z.number().int().nullable().default(null),
    /** Где остановился прогресс этого зрителя по этому тайтлу. */
    positionMs: z.number().int().nullable().default(null),
    durationMs: z.number().int().nullable().default(null),
    percent: z.number().nullable().default(null),
});
export const monitorEventSchema = z.object({
    at: z.string(),
    kind: z.enum(['progress', 'completed', 'favorite', 'search', 'channel']),
    userId: z.string(),
    userName: z.string(),
    /** Заголовок события: название тайтла или сам поисковый запрос. */
    title: z.string(),
    detail: z.string().nullable().default(null),
    contentId: z.string().nullable().default(null),
});
export const monitorUserSchema = z.object({
    id: z.string(),
    name: z.string(),
    watchingNow: z.number().int().default(0),
    favorites: z.number().int().default(0),
    /** Последние просмотры: свежие первыми. */
    history: z.array(z.object({
        contentId: z.string(),
        title: z.string(),
        subtitle: z.string().nullable().default(null),
        percent: z.number().default(0),
        completed: z.boolean().default(false),
        updatedAt: z.string(),
        /**
         * Что это было. У эфира нет ни длительности, ни процента: канал не
         * «досматривают», его включают и выключают, — и мерить его той же полосой
         * значило бы рисовать вечный ноль. Признак разводит эти две истории.
         */
        kind: z.enum(['title', 'channel']).default('title'),
        /** Подпись вместо процента там, где процента не бывает («48 мин»). */
        detail: z.string().nullable().default(null),
        /**
         * Чем смотрели в последний раз: платформа и версия приложения, как клиент
         * назвал её сам («tv 0.3.73», «web 0.1.0»).
         *
         * Запоминается в самой записи истории, а не берётся у устройства: приставка
         * обновляется сама, и версия устройства на сейчас не отвечает, какой сборкой
         * смотрели фильм, на который пожаловались. `null` — запись старше этой
         * отметки или клиент о себе не сказал.
         */
        appVersion: z.string().nullable().default(null),
        platform: devicePlatformSchema.nullable().default(null),
    })).default([]),
});
export const adminMonitorResponseSchema = z.object({
    generatedAt: z.string(),
    watching: z.array(monitorWatchSchema).default([]),
    events: z.array(monitorEventSchema).default([]),
    users: z.array(monitorUserSchema).default([]),
});
//# sourceMappingURL=user.js.map